// ./src/components/features/Connectors/ConfigEditors/SmtpConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Alert,
  Switch,
  Group,
  Box,
  List,
  Button,
  ActionIcon,
  Paper,
  NumberInput,
  Select,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconPlus,
  IconTrash,
  IconServer,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { SmtpConnectorConfig } from "@/types";
import { AccountEmailSelector } from "@/components/features/AccountEmails/AccountEmailSelector";

export interface SmtpConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: SmtpConnectorConfig = {
  host: "",
  port: 587,
  secure: false,
  username: "",
  password: "",
  fromAccountEmailId: "",
  customHeaders: {},
  rejectUnauthorized: true,
};

// Common SMTP presets
const SMTP_PRESETS = [
  { value: "custom", label: "Custom SMTP Server" },
  { value: "gmail", label: "Gmail", host: "smtp.gmail.com", port: 587 },
  {
    value: "outlook",
    label: "Outlook / Microsoft 365",
    host: "smtp.office365.com",
    port: 587,
  },
  {
    value: "yahoo",
    label: "Yahoo Mail",
    host: "smtp.mail.yahoo.com",
    port: 587,
  },
  {
    value: "sendgrid",
    label: "SendGrid SMTP",
    host: "smtp.sendgrid.net",
    port: 587,
  },
  { value: "mailgun", label: "Mailgun", host: "smtp.mailgun.org", port: 587 },
  {
    value: "amazon-ses",
    label: "Amazon SES",
    host: "email-smtp.us-east-1.amazonaws.com",
    port: 587,
  },
  {
    value: "postmark",
    label: "Postmark",
    host: "smtp.postmarkapp.com",
    port: 587,
  },
];

/**
 * Parse and validate SMTP connector configuration
 */
const parseSmtpConfig = (jsonString: string): SmtpConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  // Ensure objects exist
  if (!parsed.customHeaders) parsed.customHeaders = {};
  return parsed;
};

export const SmtpConnectorConfigEditor: React.FC<
  SmtpConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<SmtpConnectorConfig>(() =>
    parseSmtpConfig(value)
  );

  // Local state for preset selection
  const [selectedPreset, setSelectedPreset] = useState<string>("custom");

  // Local state for adding new custom header
  const [newHeaderKey, setNewHeaderKey] = useState("");
  const [newHeaderValue, setNewHeaderValue] = useState("");

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseSmtpConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: SmtpConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle preset selection
  const handlePresetChange = (presetValue: string | null) => {
    if (!presetValue) return;

    setSelectedPreset(presetValue);

    if (presetValue === "custom") return;

    const preset = SMTP_PRESETS.find((p) => p.value === presetValue);
    if (preset && "host" in preset) {
      updateConfig({
        ...config,
        host: preset.host || "",
        port: preset.port,
        secure: false,
      });
    }
  };

  // Handle field changes
  const handleHostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPreset("custom");
    updateConfig({ ...config, host: e.target.value });
  };

  const handlePortChange = (val: string | number) => {
    const port = typeof val === "string" ? parseInt(val, 10) : val;
    if (!isNaN(port)) {
      updateConfig({ ...config, port });
    }
  };

  const handleSecureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, secure: e.currentTarget.checked });
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, username: e.target.value });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, password: e.target.value });
  };

  const handleFromAccountEmailIdChange = (accountEmailId: string | null) => {
    updateConfig({ ...config, fromAccountEmailId: accountEmailId || "" });
  };

  const handleRejectUnauthorizedChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, rejectUnauthorized: e.currentTarget.checked });
  };

  // Custom headers management
  const handleAddCustomHeader = () => {
    const key = newHeaderKey.trim();
    const val = newHeaderValue.trim();
    if (key && val) {
      const customHeaders = { ...(config.customHeaders || {}), [key]: val };
      updateConfig({ ...config, customHeaders });
      setNewHeaderKey("");
      setNewHeaderValue("");
    }
  };

  const handleRemoveCustomHeader = (key: string) => {
    const customHeaders = { ...(config.customHeaders || {}) };
    delete customHeaders[key];
    updateConfig({ ...config, customHeaders });
  };

  return (
    <Stack gap="md">
      {/* Info Alert */}
      <Alert icon={<IconServer size={16} />} color="blue" variant="light">
        <Text size="sm">
          Configure your SMTP server settings. You can select a preset or enter
          custom server details.
        </Text>
      </Alert>

      {/* Preset Selection */}
      <Select
        label="SMTP Provider"
        placeholder="Select a provider or use custom"
        data={SMTP_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
        value={selectedPreset}
        onChange={handlePresetChange}
        disabled={disabled}
        size="md"
        allowDeselect={false}
      />

      {/* Host Field */}
      <TextInput
        label="SMTP Host"
        placeholder="smtp.example.com"
        description="SMTP server hostname"
        value={config.host}
        onChange={handleHostChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Port and Secure Row */}
      <Group grow align="flex-start">
        <NumberInput
          label="Port"
          placeholder="587"
          description="Common: 587 (STARTTLS), 465 (SSL)"
          value={config.port || 587}
          onChange={handlePortChange}
          disabled={disabled}
          required
          size="md"
          min={1}
          max={65535}
        />
        <Box>
          <Text size="sm" fw={500} mb={4}>
            Security
          </Text>
          <Paper withBorder p="sm">
            <Group justify="space-between">
              <div>
                <Text size="sm">Use SSL/TLS</Text>
                <Text size="xs" c="dimmed">
                  Enable for port 465
                </Text>
              </div>
              <Switch
                checked={config.secure === true}
                onChange={handleSecureChange}
                disabled={disabled}
              />
            </Group>
          </Paper>
        </Box>
      </Group>

      {/* Username Field */}
      <TextInput
        label="Username"
        placeholder="user@example.com"
        description="SMTP authentication username (usually your email)"
        value={config.username}
        onChange={handleUsernameChange}
        disabled={disabled}
        required
        size="md"
        autoComplete="off"
      />

      {/* Password Field */}
      <TextInput
        label="Password"
        placeholder="••••••••"
        description="SMTP password or app-specific password"
        value={config.password}
        onChange={handlePasswordChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Gmail/Outlook Note */}
      {(selectedPreset === "gmail" || selectedPreset === "outlook") && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="yellow"
          variant="light"
        >
          <Text size="sm" fw={500} mb="xs">
            Important for {selectedPreset === "gmail" ? "Gmail" : "Outlook"}:
          </Text>
          <List size="sm" spacing={4}>
            {selectedPreset === "gmail" && (
              <>
                <List.Item>
                  Enable <strong>2-Step Verification</strong> in your Google
                  Account
                </List.Item>
                <List.Item>
                  Create an <strong>App Password</strong> at{" "}
                  <Text component="span" c="blue" size="sm">
                    myaccount.google.com/apppasswords
                  </Text>
                </List.Item>
                <List.Item>
                  Use the App Password instead of your regular password
                </List.Item>
              </>
            )}
            {selectedPreset === "outlook" && (
              <>
                <List.Item>
                  For personal accounts, enable SMTP in Outlook settings
                </List.Item>
                <List.Item>
                  For Microsoft 365, ensure SMTP AUTH is enabled for your tenant
                </List.Item>
                <List.Item>
                  You may need an App Password if 2FA is enabled
                </List.Item>
              </>
            )}
          </List>
        </Alert>
      )}

      {/* From Account Email Field */}
      <AccountEmailSelector
        label="Sender Email"
        placeholder="Select a verified email"
        description="Choose a confirmed account email to use as the sender address"
        value={config.fromAccountEmailId}
        onChange={handleFromAccountEmailIdChange}
        disabled={disabled}
        required
        size="md"
        confirmedOnly
        emptyText="No confirmed emails available"
      />

      {/* Custom Headers Section */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Custom Headers (Optional)
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Add custom email headers (e.g., X-Custom-Header). Values support{" "}
          <code>{"{{varName}}"}</code> placeholders.
        </Text>

        {/* Existing custom headers */}
        {Object.keys(config.customHeaders || {}).length > 0 && (
          <Stack gap="xs" mb="sm">
            {Object.entries(config.customHeaders || {}).map(([key, val]) => (
              <Group key={key} gap="xs">
                <Paper withBorder p="xs" style={{ flex: 1 }} bg="gray.0">
                  <Text size="sm">
                    <strong>{key}:</strong> {val}
                  </Text>
                </Paper>
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => handleRemoveCustomHeader(key)}
                  disabled={disabled}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        )}

        {/* Add new custom header */}
        <Group gap="xs" align="flex-end">
          <TextInput
            placeholder="X-Custom-Header"
            value={newHeaderKey}
            onChange={(e) => setNewHeaderKey(e.target.value)}
            disabled={disabled}
            size="sm"
            style={{ flex: 1 }}
          />
          <TextInput
            placeholder="Value (e.g., {{formId}})"
            value={newHeaderValue}
            onChange={(e) => setNewHeaderValue(e.target.value)}
            disabled={disabled}
            size="sm"
            style={{ flex: 2 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddCustomHeader();
              }
            }}
          />
          <Button
            size="sm"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddCustomHeader}
            disabled={
              disabled || !newHeaderKey.trim() || !newHeaderValue.trim()
            }
          >
            Add
          </Button>
        </Group>
      </Box>

      {/* TLS Certificate Verification */}
      <Box>
        <Group justify="space-between">
          <div>
            <Text size="sm">Verify TLS Certificate</Text>
            <Text size="xs" c="dimmed">
              Disable for self-signed certificates (not recommended)
            </Text>
          </div>
          <Switch
            checked={config.rejectUnauthorized !== false}
            onChange={handleRejectUnauthorizedChange}
            disabled={disabled}
          />
        </Group>
      </Box>

      {/* Info about usage */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            This connector configures the SMTP connection settings
          </List.Item>
          <List.Item>
            Recipients, subject, and body are configured in the workflow
          </List.Item>
          <List.Item>
            Each recipient receives an individual email for personalization
          </List.Item>
        </List>
      </Alert>
    </Stack>
  );
};
